import { getFieldConfig } from './searchEntityFieldConfigs.js';
import {
  companyWordMatchesSearchTerm,
  personNameWordMatchesSearchTerm,
} from './searchPhoneticFr.js';

const COMPANY_PHONETIC_FIELD_IDS = new Set([
  'name_commercial',
  'reason_social',
  'big_account',
  'company_name',
  'company_reason_social',
]);

const ACCENT_MAP = {
  a: '[aàáâãäå]',
  e: '[eéèêë]',
  i: '[iíìîï]',
  o: '[oóòôõö]',
  u: '[uúùûü]',
  c: '[cç]',
  n: '[nñ]',
  y: '[yýÿ]',
};

const escapeRegex = (str) => String(str).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const accentInsensitivePattern = (str) =>
  [...String(str)].map((ch) => ACCENT_MAP[ch.toLowerCase()] || escapeRegex(ch)).join('');

const normalizeForMatch = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const MAX_HIGHLIGHT_TEXT_LENGTH = 4096;
const FUZZY_MIN_TERM_LENGTH = 3;

const NAME_SPLIT_CONFIGS = {
  missionList: {
    firstnameId: 'candidate_firstname',
    lastnameId: 'candidate_lastname',
  },
  missionCommandPalette: {
    firstnameId: 'candidate_firstname',
    lastnameId: 'candidate_lastname',
  },
  tempWorker: {
    firstnameId: 'firstname',
    lastnameId: 'lastname',
  },
  clientContact: {
    firstnameId: 'firstname',
    lastnameId: 'lastname',
  },
};

const NAME_SPLIT_CONTEXTS = new Set(Object.keys(NAME_SPLIT_CONFIGS));

const resolvePhoneticMode = (field) => {
  if (!field?.fuzzy || field.type !== 'text') return null;
  return COMPANY_PHONETIC_FIELD_IDS.has(field.id) ? 'company' : 'person';
};

const wordMatchesPhoneticSearch = (word, term, phoneticMode) => {
  if (!phoneticMode || !word || !term) return false;

  const normalizedTerm = normalizeForMatch(term);
  const normalizedWord = normalizeForMatch(word);
  if (
    normalizedTerm.length >= 4 &&
    normalizedWord.length >= 4 &&
    Math.abs(normalizedTerm.length - normalizedWord.length) > 2
  ) {
    return false;
  }

  return phoneticMode === 'company'
    ? companyWordMatchesSearchTerm(term, word)
    : personNameWordMatchesSearchTerm(term, word);
};

const getFieldHighlightTerms = (rawValue, searchTerm, field) => {
  const stringValue = String(rawValue ?? '').trim();
  if (!stringValue || !searchTerm) return [];

  if (fieldMatchesSearch(stringValue, searchTerm, { type: field.type, fuzzy: false })) {
    return [searchTerm];
  }

  const phoneticMode = resolvePhoneticMode(field);
  const tokens = extractTextTokens(stringValue);
  const matchedDisplayTokens = [];

  for (const token of tokens) {
    if (fieldMatchesSearch(token.value, searchTerm, { type: field.type, fuzzy: false })) {
      matchedDisplayTokens.push(token.value);
      continue;
    }

    if (
      field.fuzzy &&
      (fieldMatchesSearch(token.value, searchTerm, {
        type: field.type,
        fuzzy: true,
        phoneticMode,
      }) ||
        wordMatchesPhoneticSearch(token.value, searchTerm, phoneticMode))
    ) {
      matchedDisplayTokens.push(token.value);
    }
  }

  if (matchedDisplayTokens.length > 0) {
    return [...new Set(matchedDisplayTokens)];
  }

  if (
    field.fuzzy &&
    (fieldMatchesSearch(stringValue, searchTerm, {
      type: field.type,
      fuzzy: true,
      phoneticMode,
    }) ||
      tokenizeNameWords(stringValue).some((word) =>
        wordMatchesPhoneticSearch(word, searchTerm, phoneticMode)
      ))
  ) {
    if (tokens.length === 1) return [tokens[0].value];
    return [stringValue];
  }

  return [];
};

const tokenizeNameWords = (value) =>
  normalizeForMatch(value)
    .split(/[\s-]+/)
    .filter(Boolean);

const buildNormalizedIndexMap = (text) => {
  let normalized = '';
  const indexMap = [];

  for (let i = 0; i < text.length; i += 1) {
    const normalizedChar = normalizeForMatch(text[i]);
    for (let j = 0; j < normalizedChar.length; j += 1) {
      normalized += normalizedChar[j];
      indexMap.push(i);
    }
  }

  return { normalized, indexMap };
};

const mapNormalizedRangeToOriginal = (indexMap, start, length) => {
  if (length <= 0 || start >= indexMap.length) return null;
  const endIndex = start + length - 1;
  if (endIndex >= indexMap.length) return null;
  return {
    start: indexMap[start],
    end: indexMap[endIndex] + 1,
  };
};

const findSubstringHighlightRanges = (text, term) => {
  const normalizedTerm = normalizeForMatch(term);
  if (!normalizedTerm) return [];

  const { normalized, indexMap } = buildNormalizedIndexMap(text);
  const ranges = [];
  let fromIndex = 0;

  while (fromIndex <= normalized.length - normalizedTerm.length) {
    const matchIndex = normalized.indexOf(normalizedTerm, fromIndex);
    if (matchIndex === -1) break;

    const range = mapNormalizedRangeToOriginal(
      indexMap,
      matchIndex,
      normalizedTerm.length
    );
    if (range) ranges.push(range);

    fromIndex = matchIndex + 1;
  }

  return ranges;
};

const findPhoneticTokenHighlightRanges = (text, term, phoneticMode = 'person') => {
  if (!term || !phoneticMode) return [];

  const ranges = [];
  for (const token of extractTextTokens(text)) {
    if (wordMatchesPhoneticSearch(token.value, term, phoneticMode)) {
      ranges.push({ start: token.start, end: token.end });
    }
  }
  return ranges;
};

const findTokenHighlightRanges = (
  text,
  term,
  { fuzzy = false, phoneticMode = null } = {}
) => {
  const ranges = [];
  const tokens = extractTextTokens(text);
  let guard = 0;

  for (const token of tokens) {
    if (guard++ > text.length + 1) break;

    if (
      fieldMatchesSearch(token.value, term, { type: 'text', fuzzy: false }) ||
      (fuzzy && fieldMatchesSearch(token.value, term, { type: 'text', fuzzy: true }))
    ) {
      ranges.push({ start: token.start, end: token.end });
    }
  }

  if (fuzzy && ranges.length === 0) {
    const resolvedMode = phoneticMode || 'person';
    ranges.push(...findPhoneticTokenHighlightRanges(text, term, resolvedMode));
    if (ranges.length === 0 && resolvedMode === 'person') {
      ranges.push(...findPhoneticTokenHighlightRanges(text, term, 'company'));
    }
  }

  return ranges;
};

const extractTextTokens = (text) => {
  const tokens = [];
  const tokenPattern = /[\p{L}\p{N}@.+_-]+/gu;
  let match = tokenPattern.exec(text);
  let guard = 0;

  while (match && guard++ < text.length + 1) {
    tokens.push({
      value: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
    match = tokenPattern.exec(text);
  }

  return tokens;
};

const findCompoundNameHighlightRanges = (text, term, { fuzzy = false } = {}) => {
  const termParts = String(term).split(/[\s-]+/).filter(Boolean);
  if (termParts.length <= 1) return [];

  const tokens = extractTextTokens(text);
  if (tokens.length < termParts.length) return [];

  for (let startIndex = 0; startIndex <= tokens.length - termParts.length; startIndex += 1) {
    const ranges = [];
    let matched = true;

    for (let partIndex = 0; partIndex < termParts.length; partIndex += 1) {
      const part = termParts[partIndex];
      const token = tokens[startIndex + partIndex];

      const tokenMatches =
        fieldMatchesSearch(token.value, part, { type: 'text', fuzzy: false }) ||
        (fuzzy && fieldMatchesSearch(token.value, part, { type: 'text', fuzzy: true }));

      if (!tokenMatches) {
        matched = false;
        break;
      }

      ranges.push({ start: token.start, end: token.end });
    }

    if (matched) {
      return mergeAdjacentHighlightRanges(ranges, text);
    }
  }

  return [];
};

const findPrefixHighlightRanges = (text, prefix) => {
  const normalizedPrefix = normalizeForMatch(prefix);
  if (!normalizedPrefix) return [];

  const ranges = [];
  const tokenPattern = /[\p{L}\p{N}@.+_-]+/gu;
  let match = tokenPattern.exec(text);
  let guard = 0;

  while (match && guard++ < text.length + 1) {
    if (normalizeForMatch(match[0]).startsWith(normalizedPrefix)) {
      ranges.push({ start: match.index, end: match.index + match[0].length });
    }
    match = tokenPattern.exec(text);
  }

  return ranges;
};

const findPhoneHighlightRangesByDigitIndex = (text, variant) => {
  if (variant.length < 4) return null;

  const digitIndexes = [];
  for (let i = 0; i < text.length; i += 1) {
    if (/\d/.test(text[i])) digitIndexes.push(i);
  }

  if (digitIndexes.length < variant.length) return null;

  const allDigits = digitIndexes.map((index) => text[index]).join('');
  const matchIndex = allDigits.indexOf(variant);
  if (matchIndex === -1) return null;

  return {
    start: digitIndexes[matchIndex],
    end: digitIndexes[matchIndex + variant.length - 1] + 1,
  };
};

const findPhoneHighlightRanges = (text, term) => {
  const stringText = String(text || '');
  const variants = getPhoneDigitVariants(term).sort((a, b) => b.length - a.length);

  for (const variant of variants) {
    const digitRange = findPhoneHighlightRangesByDigitIndex(stringText, variant);
    if (digitRange) return [digitRange];

    try {
      const regex = buildFlexibleDigitsRegex(variant);
      const match = regex.exec(stringText);
      if (match?.[0]) {
        return [{ start: match.index, end: match.index + match[0].length }];
      }
    } catch {
      // ignore invalid regex
    }
  }

  return [];
};

const isPhoneLikeTerm = (term) => {
  const trimmed = String(term || '').trim();
  const termDigits = digitsOnly(trimmed);
  return termDigits.length >= 4 && termDigits.length === trimmed.replace(/[\s.()-]/g, '').length;
};

const mergeHighlightRanges = (ranges) => {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort(
    (a, b) => a.start - b.start || b.end - a.end - (a.end - a.start)
  );
  const merged = [];

  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (!last || range.start >= last.end) {
      merged.push({ ...range });
    } else if (range.end > last.end) {
      last.end = range.end;
    }
  }

  return merged;
};

const mergeAdjacentHighlightRanges = (ranges, text) => {
  const merged = mergeHighlightRanges(ranges);
  if (!text || merged.length <= 1) return merged;

  const combined = [{ ...merged[0] }];

  for (let index = 1; index < merged.length; index += 1) {
    const previous = combined[combined.length - 1];
    const current = merged[index];
    const gap = text.slice(previous.end, current.start);

    if (/^[\s\-–—,./]*$/.test(gap)) {
      previous.end = current.end;
      continue;
    }

    combined.push({ ...current });
  }

  return combined;
};

const buildFlexibleDigitsRegex = (digits) =>
  new RegExp(digits.split('').join('\\D*'), 'i');

const getPhoneDigitVariants = (term) => {
  const digits = digitsOnly(term);
  if (digits.length < 4) return [];

  const variants = new Set([digits]);

  if (digits.startsWith('0')) {
    variants.add(digits.slice(1));
  }

  if (digits.startsWith('596')) {
    const nationalPart = digits.slice(3);
    variants.add(nationalPart);
    variants.add(nationalPart.startsWith('0') ? nationalPart : `0${nationalPart}`);
  }

  if (digits.startsWith('33')) {
    const nationalPart = digits.slice(2);
    variants.add(nationalPart);
    variants.add(nationalPart.startsWith('0') ? nationalPart : `0${nationalPart}`);
  }

  return [...variants];
};

const phoneValuesMatch = (value, term) => {
  const stringValue = String(value || '');
  const valueDigits = digitsOnly(stringValue);
  const variants = getPhoneDigitVariants(term);

  for (const variant of variants) {
    if (variant.length < 4) continue;
    if (valueDigits.includes(variant) || variant.includes(valueDigits)) return true;
    try {
      if (buildFlexibleDigitsRegex(variant).test(stringValue)) return true;
    } catch {
      // ignore invalid regex
    }
  }

  return false;
};

const damerauLevenshteinDistance = (left, right) => {
  const a = normalizeForMatch(left);
  const b = normalizeForMatch(right);
  const alen = a.length;
  const blen = b.length;

  if (alen === 0) return blen;
  if (blen === 0) return alen;

  const matrix = Array.from({ length: alen + 1 }, () => Array(blen + 1).fill(0));

  for (let i = 0; i <= alen; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= blen; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= alen; i += 1) {
    for (let j = 1; j <= blen; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + cost);
      }
    }
  }

  return matrix[alen][blen];
};

const wordMatchesTermWithTolerance = (word, term, maxDistance = 1) => {
  const normalizedWord = normalizeForMatch(word);
  const normalizedTerm = normalizeForMatch(term);
  if (!normalizedTerm) return false;
  if (normalizedWord.includes(normalizedTerm)) return true;
  if (normalizedTerm.length < FUZZY_MIN_TERM_LENGTH) return false;
  return damerauLevenshteinDistance(normalizedWord, normalizedTerm) <= maxDistance;
};

const textMatchesTermWithTolerance = (value, term, maxDistance = 1) => {
  const normalizedValue = normalizeForMatch(value);
  const normalizedTerm = normalizeForMatch(term);
  if (!normalizedTerm) return false;
  if (normalizedValue.includes(normalizedTerm)) return true;
  if (normalizedTerm.length < FUZZY_MIN_TERM_LENGTH) return false;

  const words = normalizedValue.split(/[\s-]+/).filter(Boolean);
  if (words.some((word) => wordMatchesTermWithTolerance(word, term, maxDistance))) {
    return true;
  }

  return damerauLevenshteinDistance(normalizedValue, normalizedTerm) <= maxDistance;
};

const isExactSubstringMatch = (text, term) => {
  const normalizedValue = normalizeForMatch(text);
  const normalizedTerm = normalizeForMatch(term);
  return Boolean(normalizedTerm) && normalizedValue.includes(normalizedTerm);
};

const isFuzzyOnlyMatch = (text, term) =>
  !isExactSubstringMatch(text, term) &&
  textMatchesTermWithTolerance(text, term, 1);

const extractHighlightTerms = (search) => {
  const raw = String(search || '')
    .replace(/[()"]/g, ' ')
    .trim();
  if (!raw) return [];

  const stopWords = new Set(['et', 'ou', 'non']);
  const terms = raw
    .split(/[\s-]+/)
    .map((term) => term.trim())
    .filter((term) => term && !stopWords.has(normalizeForMatch(term)));

  return [...new Set(terms)];
};

const fieldMatchesSearch = (
  value,
  term,
  { type = 'text', fuzzy = false, multiTerm = false, phoneticMode = null } = {}
) => {
  if (value === null || value === undefined || value === '') return false;
  const stringValue = String(value);
  const normalizedValue = normalizeForMatch(stringValue);
  const normalizedTerm = normalizeForMatch(term);

  if (!normalizedTerm) return false;

  if (type === 'phone') {
    return phoneValuesMatch(stringValue, term);
  }

  if (type === 'number') {
    const termDigits = digitsOnly(term);
    if (termDigits.length >= 2 && digitsOnly(stringValue).includes(termDigits)) return true;
    if (normalizedValue.includes(normalizedTerm)) return true;
    return String(value) === String(term);
  }

  if (normalizedValue.includes(normalizedTerm)) return true;

  if (term.endsWith('*') || normalizedTerm.length >= 2) {
    const prefix = term.endsWith('*') ? term.slice(0, -1) : normalizedTerm;
    const prefixPattern = new RegExp(`^${accentInsensitivePattern(prefix)}`, 'i');
    if (prefixPattern.test(stringValue)) return true;
  }

  const allowFuzzy = fuzzy && normalizedTerm.length >= FUZZY_MIN_TERM_LENGTH;

  if (allowFuzzy && textMatchesTermWithTolerance(stringValue, term, 1)) {
    return true;
  }

  if (allowFuzzy && phoneticMode) {
    const words = tokenizeNameWords(stringValue);
    if (words.some((word) => wordMatchesPhoneticSearch(word, term, phoneticMode))) {
      return true;
    }
  }

  return false;
};

const termMatchesFieldValue = (rawValue, term, field, multiTerm = false) => {
  const phoneticMode = resolvePhoneticMode(field);
  return (
    fieldMatchesSearch(rawValue, term, {
      type: field.type,
      fuzzy: field.fuzzy,
      multiTerm,
      phoneticMode,
    }) ||
    (field.fuzzy &&
      tokenizeNameWords(rawValue).some((word) =>
        wordMatchesPhoneticSearch(word, term, phoneticMode)
      ))
  );
};

const getPersonNameSplitMatch = (item, fields, terms, context) => {
  const config = NAME_SPLIT_CONFIGS[context];
  if (!config || terms.length !== 2) return null;

  const firstnameField = fields.find((field) => field.id === config.firstnameId);
  const lastnameField = fields.find((field) => field.id === config.lastnameId);
  if (!firstnameField || !lastnameField) return null;

  const firstname = firstnameField.getValue(item);
  const lastname = lastnameField.getValue(item);
  if (!firstname && !lastname) return null;

  const orientations = [
    { firstTerm: terms[0], lastTerm: terms[1] },
    { firstTerm: terms[1], lastTerm: terms[0] },
  ];

  for (const { firstTerm, lastTerm } of orientations) {
    if (
      termMatchesFieldValue(firstname, firstTerm, firstnameField, true) &&
      termMatchesFieldValue(lastname, lastTerm, lastnameField, true)
    ) {
      return {
        firstnameField,
        lastnameField,
        firstname,
        lastname,
        firstTerm,
        lastTerm,
        firstnameId: config.firstnameId,
        lastnameId: config.lastnameId,
      };
    }
  }

  return null;
};

const findHighlightRanges = (text, terms, { fuzzy = false, phoneticMode = null } = {}) => {
  if (!text || !terms?.length) return [];

  const stringText = String(text);
  if (stringText.length > MAX_HIGHLIGHT_TEXT_LENGTH) return [];

  const ranges = [];
  const tokenHighlightOptions = { fuzzy, phoneticMode };

  for (const term of [...terms].sort((a, b) => b.length - a.length)) {
    if (!term) continue;

    if (isPhoneLikeTerm(term)) {
      ranges.push(...findPhoneHighlightRanges(stringText, term));
      continue;
    }

    const exactMatch = isExactSubstringMatch(stringText, term);
    const termParts = String(term).split(/[\s-]+/).filter(Boolean);
    const isCompoundTerm = termParts.length > 1;
    let termRanges = [];

    if (exactMatch) {
      termRanges = findSubstringHighlightRanges(stringText, term);
    } else if (isCompoundTerm) {
      termRanges = findCompoundNameHighlightRanges(stringText, term, {
        fuzzy: true,
      });
    } else {
      const fuzzyOnlyMatch = isFuzzyOnlyMatch(stringText, term);
      const isSingleUnitText = !/\s/.test(stringText.trim());

      if (fuzzyOnlyMatch && isSingleUnitText) {
        termRanges = [{ start: 0, end: stringText.length }];
      } else if (fuzzyOnlyMatch) {
        termRanges = findTokenHighlightRanges(stringText, term, {
          ...tokenHighlightOptions,
          fuzzy: true,
        });
        if (termRanges.length === 0 && textMatchesTermWithTolerance(stringText, term, 1)) {
          termRanges = [{ start: 0, end: stringText.length }];
        }
      } else {
        termRanges = [
          ...findSubstringHighlightRanges(stringText, term),
          ...findTokenHighlightRanges(stringText, term, tokenHighlightOptions),
        ];

        if (term.endsWith('*')) {
          const prefix = term.slice(0, -1);
          if (prefix) {
            termRanges.push(...findPrefixHighlightRanges(stringText, prefix));
          }
        }
      }
    }

    if (fuzzy && termRanges.length === 0 && !isPhoneLikeTerm(term)) {
      const resolvedMode = phoneticMode || 'person';
      termRanges = findPhoneticTokenHighlightRanges(stringText, term, resolvedMode);
      if (termRanges.length === 0 && resolvedMode === 'person') {
        termRanges = findPhoneticTokenHighlightRanges(stringText, term, 'company');
      }
    }

    ranges.push(...termRanges);
  }

  return mergeAdjacentHighlightRanges(ranges, stringText);
};

const formatMatchHint = (labelFr, value) => {
  const displayValue = String(value || '').trim();
  if (!displayValue) return `Trouvé "${labelFr}"`;
  return `Trouvé "${labelFr} ${displayValue}"`;
};

const formatFieldDisplayValue = (field, rawValue) => {
  if (field.type === 'number' && rawValue !== null && rawValue !== undefined && rawValue !== '') {
    return String(rawValue);
  }
  return String(rawValue || '').trim();
};

const fieldValuesOverlap = (left, right) => {
  const normalizedLeft = normalizeForMatch(left);
  const normalizedRight = normalizeForMatch(right);
  if (!normalizedLeft || !normalizedRight) return false;
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.includes(normalizedRight) ||
    normalizedRight.includes(normalizedLeft)
  );
};

const isHiddenMatchAlreadyVisible = (displayValue, matchedTerms, matchedVisibleFields) =>
  matchedVisibleFields.some(
    ({ value, terms }) =>
      matchedTerms.some((term) => terms.includes(term)) &&
      fieldValuesOverlap(value, displayValue)
  );

const pushHiddenFieldHint = (
  matchHints,
  field,
  displayValue,
  matchedTerms,
  matchedVisibleFields
) => {
  if (isHiddenMatchAlreadyVisible(displayValue, matchedTerms, matchedVisibleFields)) {
    return;
  }
  matchHints.push(formatMatchHint(field.labelFr, displayValue));
};

const buildMatchPresentation = ({ search, item, context, includeCvHeuristic = false }) => {
  const terms = extractHighlightTerms(search);
  const emptySlots = {};

  if (terms.length === 0 || String(search || '').trim().length < 2) {
    return { matchHints: [], slotTerms: emptySlots, terms: [] };
  }

  const fields = getFieldConfig(context);
  const slotTerms = {};
  const matchHints = [];
  const matchedFields = [];
  const matchedVisibleFields = [];
  let hasNonCvMatch = false;
  const multiTerm = terms.length > 1;
  const useNameSplit = NAME_SPLIT_CONTEXTS.has(context) && terms.length === 2;
  const nameSplitMatch = useNameSplit ? getPersonNameSplitMatch(item, fields, terms, context) : null;
  const nameSplitFieldIds = nameSplitMatch
    ? new Set([nameSplitMatch.firstnameId, nameSplitMatch.lastnameId])
    : null;
  const nameSplitVisibleSlot = nameSplitMatch?.firstnameField?.visibleIn || null;

  for (const field of fields) {
    if (field.cvOnly) continue;

    const rawValue = field.getValue(item);
    if (rawValue === null || rawValue === undefined || rawValue === '') continue;

    if (
      nameSplitMatch &&
      field.visibleIn &&
      field.visibleIn !== nameSplitVisibleSlot &&
      !nameSplitFieldIds.has(field.id)
    ) {
      continue;
    }

    if (useNameSplit && nameSplitFieldIds?.has(field.id)) {
      if (!nameSplitMatch) continue;

      const matchedTerm =
        field.id === nameSplitMatch.firstnameId
          ? nameSplitMatch.firstTerm
          : nameSplitMatch.lastTerm;

      if (!termMatchesFieldValue(rawValue, matchedTerm, field, true)) continue;

      hasNonCvMatch = true;
      const displayValue = formatFieldDisplayValue(field, rawValue);
      const highlightTerms = getFieldHighlightTerms(rawValue, matchedTerm, field);
      const termsForSlot =
        highlightTerms.length > 0
          ? highlightTerms
          : extractTextTokens(String(rawValue ?? ''))
              .filter((token) => termMatchesFieldValue(token.value, matchedTerm, field, true))
              .map((token) => token.value);

      matchedFields.push({
        id: field.id,
        labelFr: field.labelFr,
        rawValue: displayValue,
        matchedTerms: termsForSlot,
        visibleIn: field.visibleIn || null,
      });

      if (field.visibleIn) {
        if (!slotTerms[field.visibleIn]) slotTerms[field.visibleIn] = [];
        slotTerms[field.visibleIn].push(...termsForSlot);
        matchedVisibleFields.push({ value: displayValue, terms: termsForSlot });
      } else {
        pushHiddenFieldHint(
          matchHints,
          field,
          displayValue,
          termsForSlot,
          matchedVisibleFields
        );
      }
      continue;
    }

    const matchedTerms = terms.flatMap((term) => getFieldHighlightTerms(rawValue, term, field));

    if (matchedTerms.length === 0) continue;

    hasNonCvMatch = true;
    const displayValue = formatFieldDisplayValue(field, rawValue);

    matchedFields.push({
      id: field.id,
      labelFr: field.labelFr,
      rawValue: displayValue,
      matchedTerms,
      visibleIn: field.visibleIn || null,
    });

    if (field.visibleIn) {
      if (!slotTerms[field.visibleIn]) slotTerms[field.visibleIn] = [];
      slotTerms[field.visibleIn].push(...matchedTerms);
      matchedVisibleFields.push({ value: displayValue, terms: matchedTerms });
    } else {
      pushHiddenFieldHint(
        matchHints,
        field,
        displayValue,
        matchedTerms,
        matchedVisibleFields
      );
    }
  }

  if (includeCvHeuristic && !hasNonCvMatch) {
    const cvTerms = terms.filter(
      (term) => !term.includes('@') && digitsOnly(term).length < 4
    );
    if (cvTerms.length > 0) {
      const cvHint = formatMatchHint('CV', cvTerms[0]);
      matchHints.push(cvHint);
      matchedFields.push({
        id: 'cv',
        labelFr: 'CV',
        rawValue: cvTerms[0],
        matchedTerms: cvTerms,
        visibleIn: null,
        heuristic: true,
      });
    }
  }

  Object.keys(slotTerms).forEach((slot) => {
    slotTerms[slot] = [...new Set(slotTerms[slot])];
  });

  const hasVisibleHighlights = Object.keys(slotTerms).length > 0;

  return {
    matchHints: hasVisibleHighlights ? [] : [...new Set(matchHints)],
    slotTerms,
    terms,
  };
};

export {
  extractHighlightTerms,
  normalizeForMatch,
  fieldMatchesSearch,
  findHighlightRanges,
  buildMatchPresentation,
  formatMatchHint,
};
